import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isValidSlug, type BoothManifest } from '@photobooth/shared';
import {
  executeBoothDelete,
  fetchBoothManifest,
  requestBoothDelete,
} from '../api/client';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { logger } from '../utils/log';
import { trackEvent } from '../utils/analytics';

const log = logger('booth-delete');

type Mode = 'request' | 'confirm';

interface Props {
  /** 'request' = first page (HMAC token from welcome email).
   *  'confirm' = final page (OTP from the second email). */
  mode: Mode;
}

type Phase = 'loading' | 'ready' | 'busy' | 'sent' | 'deleted' | 'error';

export function BoothDeletePage({ mode }: Props) {
  const { slug = '' } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';
  const navigate = useNavigate();

  const [manifest, setManifest] = useState<BoothManifest | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!isValidSlug(slug)) {
      setPhase('error');
      setError('Invalid booth URL.');
      return;
    }
    if (!token) {
      setPhase('error');
      setError('This link is missing its security token. Open the link in the email again.');
      return;
    }
    (async () => {
      const m = await fetchBoothManifest(slug);
      if (cancelled) return;
      if (!m) {
        setPhase('error');
        setError(`Booth /${slug} is already deleted or never existed.`);
        return;
      }
      setManifest(m);
      setPhase('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const onRequest = async () => {
    setPhase('busy');
    setError(null);
    try {
      const res = await requestBoothDelete(slug, { token });
      trackEvent('booth_delete_request_sent', { slug });
      setSentTo(res.emailMasked);
      setPhase('sent');
    } catch (err) {
      log.warn('delete request failed', err);
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setPhase('error');
    }
  };

  const onConfirm = async () => {
    setPhase('busy');
    setError(null);
    try {
      await executeBoothDelete(slug, { otp: token });
      trackEvent('booth_delete_executed', { slug });
      setPhase('deleted');
    } catch (err) {
      log.warn('delete execute failed', err);
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setPhase('error');
    }
  };

  if (phase === 'loading') {
    return (
      <div style={centered}>
        <p style={{ color: 'var(--text-dim)' }}>Loading…</p>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div style={centered}>
        <h1>Can’t continue</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 380, textAlign: 'center' }}>
          {error ?? 'Something went wrong.'}
        </p>
        <Button variant="primary" onClick={() => navigate(`/${slug}`)}>
          Back to booth
        </Button>
      </div>
    );
  }
  if (phase === 'deleted') {
    return (
      <div style={centered}>
        <h1>Booth deleted</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 420, textAlign: 'center' }}>
          {manifest?.title ?? slug} is gone — every photo, video, and link.
          Make a new one any time.
        </p>
        <Button variant="primary" onClick={() => navigate('/builder')}>
          Build a new one
        </Button>
        <FooterCredit />
      </div>
    );
  }
  if (phase === 'sent') {
    return (
      <div style={centered}>
        <h1>Check your email</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 420, textAlign: 'center' }}>
          We sent a one-time confirmation link to <strong>{sentTo}</strong>.
          Click it within 15 minutes to finish the delete. Nothing is removed
          until you click that link.
        </p>
        <Button variant="ghost" onClick={() => navigate(`/${slug}`)}>
          Cancel — keep the booth
        </Button>
      </div>
    );
  }

  const isConfirm = mode === 'confirm';
  const title = isConfirm ? 'Permanently delete this booth?' : 'Delete this booth?';
  const explain = isConfirm
    ? 'This is the last step. Clicking the button below permanently removes the booth, every photo and video uploaded to it, and every share link. There is no undo.'
    : 'Before anything is removed, we’ll send a one-time confirmation link to the email address you used to create the booth. The actual delete happens when you click that link.';
  const actionLabel = isConfirm ? 'Yes — delete forever' : 'Send confirmation email';
  const action = isConfirm ? onConfirm : onRequest;
  return (
    <div style={centered}>
      <h1 style={{ color: isConfirm ? '#ff7a9d' : 'var(--text)' }}>{title}</h1>
      {manifest && (
        <p style={{ color: 'var(--text-dim)' }}>
          <strong>{manifest.title}</strong> — <code>/{manifest.slug}</code>
        </p>
      )}
      <p style={{ color: 'var(--text-dim)', maxWidth: 420, textAlign: 'center' }}>
        {explain}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="ghost" onClick={() => navigate(`/${slug}`)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={phase === 'busy'}
          onClick={() => void action()}
          style={isConfirm ? { background: '#d4143f', color: '#fff' } : undefined}
        >
          {phase === 'busy' ? 'Working…' : actionLabel}
        </Button>
      </div>
    </div>
  );
}

const centered: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  color: 'var(--text)',
};
